import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

export interface SearchFiltersState {
  courtType: string;
  dateFrom: string;
  dateTo: string;
  threshold: number;
  judgmentType: string;
  judgeName: string;
  legalBase: string;
}

interface SearchFiltersProps {
  filters: SearchFiltersState;
  onChange: (filters: SearchFiltersState) => void;
  onApply: () => void;
  isOpen: boolean;
  onToggle: () => void;
}

export default function SearchFilters({ filters, onChange, onApply, isOpen, onToggle }: SearchFiltersProps) {
  const [localFilters, setLocalFilters] = useState(filters);
  const [judgmentTypes, setJudgmentTypes] = useState<string[]>([]);
  const [judgeSuggestions, setJudgeSuggestions] = useState<string[]>([]);
  const [legalBaseSuggestions, setLegalBaseSuggestions] = useState<string[]>([]);
  const [showJudgeSuggestions, setShowJudgeSuggestions] = useState(false);
  const [showLegalBaseSuggestions, setShowLegalBaseSuggestions] = useState(false);
  const judgeInputRef = useRef<HTMLInputElement>(null);
  const legalBaseInputRef = useRef<HTMLInputElement>(null);
  const { t } = useTranslation();

  const COURT_TYPES = [
    { value: '', label: t('searchFilters.allCourts') },
    { value: 'SUPREME', label: t('searchFilters.supreme') },
    { value: 'COMMON', label: t('searchFilters.common') },
    { value: 'CONSTITUTIONAL_TRIBUNAL', label: t('searchFilters.constitutionalTribunal') },
    { value: 'NATIONAL_APPEAL_CHAMBER', label: t('searchFilters.nationalAppealChamber') },
  ];

  // Fetch judgment types on mount
  useEffect(() => {
    fetch('/api/v1/search/judgment-types')
      .then(res => res.json())
      .then(data => setJudgmentTypes(data.types || []))
      .catch(() => setJudgmentTypes([]));
  }, []);

  // Fetch judge suggestions
  useEffect(() => {
    if (localFilters.judgeName.length < 2) {
      setJudgeSuggestions([]);
      return;
    }
    const timer = setTimeout(() => {
      fetch(`/api/v1/search/suggestions/judges?q=${encodeURIComponent(localFilters.judgeName)}`)
        .then(res => res.json())
        .then(data => setJudgeSuggestions(data.suggestions || []))
        .catch(() => setJudgeSuggestions([]));
    }, 300);
    return () => clearTimeout(timer);
  }, [localFilters.judgeName]);

  // Fetch legal base suggestions
  useEffect(() => {
    if (localFilters.legalBase.length < 2) {
      setLegalBaseSuggestions([]);
      return;
    }
    const timer = setTimeout(() => {
      fetch(`/api/v1/search/suggestions/legal-bases?q=${encodeURIComponent(localFilters.legalBase)}`)
        .then(res => res.json())
        .then(data => setLegalBaseSuggestions(data.suggestions || []))
        .catch(() => setLegalBaseSuggestions([]));
    }, 300);
    return () => clearTimeout(timer);
  }, [localFilters.legalBase]);

  const handleChange = (key: keyof SearchFiltersState, value: string | number) => {
    const newFilters = { ...localFilters, [key]: value };
    setLocalFilters(newFilters);
    onChange(newFilters);
  };

  const handleApply = () => {
    onApply();
    onToggle();
  };

  const handleReset = () => {
    const defaultFilters: SearchFiltersState = {
      courtType: '',
      dateFrom: '',
      dateTo: '',
      threshold: 0.3,
      judgmentType: '',
      judgeName: '',
      legalBase: '',
    };
    setLocalFilters(defaultFilters);
    onChange(defaultFilters);
  };

  const hasActiveFilters =
    filters.courtType ||
    filters.dateFrom ||
    filters.dateTo ||
    filters.threshold !== 0.3 ||
    filters.judgmentType ||
    filters.judgeName ||
    filters.legalBase;

  const activeFilterCount = [
    filters.courtType,
    filters.dateFrom || filters.dateTo,
    filters.judgmentType,
    filters.judgeName,
    filters.legalBase,
  ].filter(Boolean).length;

  return (
    <div className="relative">
      <button
        onClick={onToggle}
        className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-colors ${
          hasActiveFilters
            ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
            : 'border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-400'
        }`}
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
          />
        </svg>
        <span className="hidden sm:inline">{t('searchFilters.filters')}</span>
        {activeFilterCount > 0 && (
          <span className="min-w-[20px] h-5 px-1.5 bg-blue-500 text-white text-xs font-medium rounded-full flex items-center justify-center">
            {activeFilterCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 mt-2 w-96 bg-white dark:bg-slate-800 rounded-xl shadow-lg border border-slate-200 dark:border-slate-700 p-4 z-50 max-h-[80vh] overflow-y-auto">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-medium text-slate-900 dark:text-white">{t('searchFilters.title')}</h3>
            {hasActiveFilters && (
              <button
                onClick={handleReset}
                className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
              >
                {t('searchFilters.reset')}
              </button>
            )}
          </div>

          <div className="space-y-4">
            {/* Court Type */}
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                {t('searchFilters.courtType')}
              </label>
              <select
                value={localFilters.courtType}
                onChange={(e) => handleChange('courtType', e.target.value)}
                className="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {COURT_TYPES.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Judgment Type */}
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                {t('searchFilters.judgmentType')}
              </label>
              <select
                value={localFilters.judgmentType}
                onChange={(e) => handleChange('judgmentType', e.target.value)}
                className="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">{t('searchFilters.allTypes')}</option>
                {judgmentTypes.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </div>

            {/* Judge Name with autocomplete */}
            <div className="relative">
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                {t('searchFilters.judge')}
              </label>
              <input
                ref={judgeInputRef}
                type="text"
                value={localFilters.judgeName}
                onChange={(e) => handleChange('judgeName', e.target.value)}
                onFocus={() => setShowJudgeSuggestions(true)}
                onBlur={() => setTimeout(() => setShowJudgeSuggestions(false), 200)}
                placeholder={t('searchFilters.judgePlaceholder')}
                className="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              {showJudgeSuggestions && judgeSuggestions.length > 0 && (
                <ul className="absolute z-10 w-full mt-1 bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg shadow-lg max-h-40 overflow-y-auto">
                  {judgeSuggestions.map((suggestion, idx) => (
                    <li
                      key={idx}
                      onClick={() => {
                        handleChange('judgeName', suggestion);
                        setShowJudgeSuggestions(false);
                      }}
                      className="px-3 py-2 cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-600 text-slate-900 dark:text-white text-sm"
                    >
                      {suggestion}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Legal Base with autocomplete */}
            <div className="relative">
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                {t('searchFilters.legalBase')}
              </label>
              <input
                ref={legalBaseInputRef}
                type="text"
                value={localFilters.legalBase}
                onChange={(e) => handleChange('legalBase', e.target.value)}
                onFocus={() => setShowLegalBaseSuggestions(true)}
                onBlur={() => setTimeout(() => setShowLegalBaseSuggestions(false), 200)}
                placeholder={t('searchFilters.legalBasePlaceholder')}
                className="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              {showLegalBaseSuggestions && legalBaseSuggestions.length > 0 && (
                <ul className="absolute z-10 w-full mt-1 bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg shadow-lg max-h-40 overflow-y-auto">
                  {legalBaseSuggestions.map((suggestion, idx) => (
                    <li
                      key={idx}
                      onClick={() => {
                        handleChange('legalBase', suggestion);
                        setShowLegalBaseSuggestions(false);
                      }}
                      className="px-3 py-2 cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-600 text-slate-900 dark:text-white text-sm"
                    >
                      {suggestion}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Date Range */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  {t('searchFilters.dateFrom')}
                </label>
                <input
                  type="date"
                  value={localFilters.dateFrom}
                  onChange={(e) => handleChange('dateFrom', e.target.value)}
                  className="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  {t('searchFilters.dateTo')}
                </label>
                <input
                  type="date"
                  value={localFilters.dateTo}
                  onChange={(e) => handleChange('dateTo', e.target.value)}
                  className="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Similarity Threshold */}
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                {t('searchFilters.similarityThreshold')}: {(localFilters.threshold * 100).toFixed(0)}%
              </label>
              <input
                type="range"
                min="0"
                max="1"
                step="0.05"
                value={localFilters.threshold}
                onChange={(e) => handleChange('threshold', parseFloat(e.target.value))}
                className="w-full h-2 bg-slate-200 dark:bg-slate-600 rounded-lg appearance-none cursor-pointer accent-blue-600"
              />
              <div className="flex justify-between text-xs text-slate-500 dark:text-slate-400 mt-1">
                <span>{t('searchFilters.loose')}</span>
                <span>{t('searchFilters.strict')}</span>
              </div>
            </div>

            {/* Apply Button */}
            <button
              onClick={handleApply}
              className="w-full mt-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors"
            >
              {t('searchFilters.apply')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
